/**
 * __OFF_SHIFT_BOARD__ (2026-09-25) who is off shift, for the Fleet Map.
 *
 * Henry: "Add that to the hauling board as well, so that when drivers are off shift it's
 * reflected there, just like in the app when I'm looking at them through my dispatch app."
 *
 * The same rule the dispatch app (1.0.37) gets from the hub (Billing-Manager
 * live_tracking.shift_roster, served with the live-map watch): a driver with a login is
 * ON shift when he has an open shift_sessions row (signed_out_at NULL), otherwise OFF,
 * "since" his latest sign-off. A driver without a login is left out -- nobody can know,
 * so his truck shows as it always did.
 *
 * Off shift his phone shares no location on purpose (driver app 1.19), so the board shows
 * him GREY at his last position from BEFORE the sign-off -- never anything later, never a
 * speed, never LIVE / STALE / OFFLINE -- with "Off shift — last seen <time>".
 *
 * Pure: no imports, so sim/offshift.test.mjs can run it as it is.
 */

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/** ms since epoch of a DB time; a zone-less string is UTC (gps_locations.timestamp). null if bad. */
export const tsMs = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = String(value).trim();
  if (!HAS_OFFSET.test(s)) s = s.replace(' ', 'T').replace(/(\.\d{3})\d+$/, '$1') + 'Z';
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
};

/** A ms time as gps_locations.timestamp holds it: naive UTC, "2026-09-25T21:04:05.000". */
export const naiveUtc = (ms) => new Date(ms).toISOString().replace('Z', '');

/**
 * {drivers.id: {on, since}} from drivers (id, user_id) and shift_sessions rows.
 * on: an open session (since = its signed_in_at); off: since = the latest signed_out_at, or null.
 */
export const shiftRoster = (drivers, openRows, closedRows) => {
  const idsByUid = {};
  (drivers || []).forEach((d) => {
    if (d && d.id != null && d.user_id) (idsByUid[String(d.user_id)] = idsByUid[String(d.user_id)] || []).push(String(d.id));
  });
  const opened = {};
  const lastOut = {};
  (openRows || []).forEach((r) => {
    const u = String((r && r.user_id) || '');
    if (idsByUid[u] && !r.signed_out_at) {
      const t = r.signed_in_at || null;
      if (!(u in opened) || String(t || '') > String(opened[u] || '')) opened[u] = t;
    }
  });
  (closedRows || []).forEach((r) => {
    const u = String((r && r.user_id) || '');
    const t = r && r.signed_out_at;
    if (idsByUid[u] && t && (!(u in lastOut) || (tsMs(t) || 0) > (tsMs(lastOut[u]) || 0))) lastOut[u] = t;
  });
  const out = {};
  Object.keys(idsByUid).forEach((u) => {
    const st = u in opened ? { on: true, since: opened[u] } : { on: false, since: lastOut[u] || null };
    idsByUid[u].forEach((id) => { out[id] = { ...st }; });
  });
  return out;
};

/** Signed off, per the shift data? (unknown driver = no) */
export const isOffShift = (shifts, driverId) =>
  !!(shifts && driverId != null && Object.prototype.hasOwnProperty.call(shifts, String(driverId))
    && shifts[String(driverId)] && shifts[String(driverId)].on === false);

/** The driver a truck's row is about: the newest ping's driver, else the one assigned to the truck. */
export const truckDriverId = (truckId, pingDriverId, drivers) => {
  if (pingDriverId != null && pingDriverId !== '') return String(pingDriverId);
  const d = (drivers || []).find((x) => x && x.current_truck_id != null && String(x.current_truck_id) === String(truckId));
  return d ? String(d.id) : null;
};

/** "5:02 PM" today, "Sep 24, 5:02 PM" otherwise (local time). */
export const clockText = (ms, nowMs = Date.now()) => {
  if (ms == null || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  const t = `${h % 12 || 12}:${m < 10 ? '0' : ''}${m} ${h < 12 ? 'AM' : 'PM'}`;
  const n = new Date(nowMs);
  if (d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate()) return t;
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MON[d.getMonth()]} ${d.getDate()}, ${t}`;
};

/** "Off shift — last seen 5:02 PM"; without a position "Off shift since 5:02 PM"; else "Off shift". */
export const offShiftText = (lastSeenMs, offSinceMs, nowMs = Date.now()) => {
  if (lastSeenMs != null && Number.isFinite(lastSeenMs)) return `Off shift — last seen ${clockText(lastSeenMs, nowMs)}`;
  if (offSinceMs != null && Number.isFinite(offSinceMs)) return `Off shift since ${clockText(offSinceMs, nowMs)}`;
  return 'Off shift';
};

export const OFF_SHIFT_COLOR = '#8b949e';

/**
 * The Fleet Map row for a truck whose driver is off shift. `ping` must be his last position
 * at or before the sign-off (or null): its place and time are the only things kept -- no
 * speed, no heading, no later ping.
 */
export const offShiftLocation = (base, ping, info, driverName, nowMs = Date.now()) => {
  const offSinceMs = tsMs(info && info.since);
  let p = ping || null;
  const pingMs = p ? tsMs(p.timestamp) : null;
  // Belt and braces: a position from after the sign-off is never shown.
  if (p && (pingMs == null || (offSinceMs != null && pingMs > offSinceMs))) p = null;
  const lastSeenMs = p ? pingMs : null;
  return {
    truckId: base && base.truckId,
    truckNumber: base && base.truckNumber,
    licensePlate: base && base.licensePlate,
    driverId: p ? String(p.driver_id || '') || null : (base && base.driverId) || null,
    driverName: driverName || '',
    lat: p ? p.latitude : null,
    lng: p ? p.longitude : null,
    speed_kmh: null,
    heading: null,
    accuracy: null,
    timestamp: p ? p.timestamp : null,
    ageMinutes: null,
    status: 'OFF SHIFT',
    statusColor: OFF_SHIFT_COLOR,
    statusDot: '⚪',
    hasData: !!p,
    offShift: true,
    offSinceMs,
    lastSeenMs,
    offText: offShiftText(lastSeenMs, offSinceMs, nowMs),
  };
};
